import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BellRing, Pin, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { listAnnouncementFeed } from "./announcementApi";
import {
  announcementReminderRefreshInterval,
  summarizeAnnouncementReminder,
} from "./announcementReminderSummary";
import type { OperatorIdentity } from "./api";

export interface AnnouncementReminderProps {
  operator: OperatorIdentity;
  enabled: boolean;
}

export function AnnouncementReminder({
  operator,
  enabled,
}: AnnouncementReminderProps): React.JSX.Element | null {
  const location = useLocation();
  const shouldLoad = enabled && location.pathname !== "/announcements";
  const context = useMemo(
    () => ({
      tenantId: operator.tenantId,
      storeId: operator.storeId,
      accountId: operator.id,
    }),
    [operator.id, operator.storeId, operator.tenantId],
  );
  const feedQuery = useQuery({
    queryKey: ["announcements", "feed", context.tenantId, context.storeId, context.accountId],
    queryFn: ({ signal }) => listAnnouncementFeed(context, signal),
    enabled: shouldLoad,
    retry: false,
    refetchInterval: (query) => announcementReminderRefreshInterval(query.state.data),
    refetchIntervalInBackground: false,
    refetchOnReconnect: "always",
  });

  if (!shouldLoad || feedQuery.isPending) return null;

  if (feedQuery.isError) {
    return (
      <aside className="announcement-reminder is-error" aria-live="polite">
        <AlertTriangle aria-hidden="true" size={18} />
        <div className="announcement-reminder-copy">
          <strong>公告提醒暂时无法核对</strong>
          <span>当前不显示旧数量，请刷新核对，或进入公告中心查看。</span>
        </div>
        <div className="announcement-reminder-actions">
          <button
            type="button"
            className="icon-button"
            aria-label="重新核对公告提醒"
            title="重新核对公告提醒"
            disabled={feedQuery.isFetching}
            onClick={() => void feedQuery.refetch()}
          >
            <RefreshCw aria-hidden="true" size={17} />
          </button>
          <Link className="button secondary" to="/announcements">
            进入公告中心
          </Link>
        </div>
      </aside>
    );
  }

  if (feedQuery.isFetching) {
    return (
      <aside className="announcement-reminder is-checking" aria-live="polite" aria-busy="true">
        <RefreshCw className="is-spinning" aria-hidden="true" size={18} />
        <div className="announcement-reminder-copy">
          <strong>正在重新核对公告提醒</strong>
          <span>核对完成前暂不显示旧数量。</span>
        </div>
        <Link className="button secondary" to="/announcements">
          进入公告中心
        </Link>
      </aside>
    );
  }

  const summary = summarizeAnnouncementReminder(feedQuery.data);
  if (summary.totalOutstanding === 0) return null;

  return (
    <aside className="announcement-reminder" aria-live="polite">
      <BellRing aria-hidden="true" size={19} />
      <div className="announcement-reminder-copy">
        <strong>有 {summary.totalOutstanding} 条公告尚未确认</strong>
        <span>
          未读 {summary.unread} 条，待确认 {summary.awaitingAcknowledgement} 条
          {summary.hasPinnedOutstanding ? (
            <span className="announcement-reminder-pinned">
              <Pin aria-hidden="true" size={14} />
              含置顶公告
            </span>
          ) : null}
        </span>
      </div>
      <Link className="button secondary" to="/announcements">
        进入公告中心
      </Link>
    </aside>
  );
}
